/**
 * A leitura genérica do boleto a partir das linhas do PDF.
 *
 * Papel: aqui ficam `lerBoleto`, que acha a linha digitável no texto e devolve
 * código, valor, vencimento e banco, e `validarLinhaDigitavel`, que confere os
 * dígitos verificadores de uma linha de 47 dígitos (bancário) ou de 48 (convênio).
 *
 * Como usar: `lerBoleto` recebe as linhas de `extrairTextoDoPdf` já separadas e
 * não abre PDF nem depende de `shared/lib/pdf`. Um campo que não pode ser lido
 * com segurança volta `null` com um aviso; nunca é estimado. Por isso a
 * conferência precisa mostrar o aviso junto do campo vazio.
 *
 * Um código com dígito verificador inválido devolve `codigo: null`, `valor: null`
 * e `vencimento: null`, mesmo que o texto traga datas, porque um código que não
 * confere não é confiável nem para o valor nem para o fator de vencimento.
 *
 * Notas: o bancário traz o vencimento no fator (quatro dígitos depois do DV geral),
 * contado a partir de 07/10/1997. Depois do reinício de 22/02/2025, o fator 1000
 * é essa data, e o fator de 1000 a 9999 se soma a 9000 dias para chegar à data
 * do ciclo atual. Os ciclos anteriores não são devolvidos. O fator 0000 significa
 * sem vencimento.
 *
 * No convênio, o terceiro dígito é o identificador de valor: 6 e 8 indicam valor
 * efetivo em reais; 7 e 9 indicam valor de referência, que não se cobra
 * diretamente e por isso é devolvido `null`. O convênio não traz o vencimento:
 * ele vem da data do texto que fica perto de "Vencimento".
 */

/** O que `lerBoleto` lê de um boleto. */
export interface BoletoLido {
  codigo: string | null;
  tipoCodigo: 'bancario' | 'convenio' | null;
  valor: number | null;
  /** O vencimento em `AAAA-MM-DD`, ou `null`. */
  vencimento: string | null;
  banco: string | null;
  avisos: string[];
}

/** Um código que casou no texto, ainda sem conferência dos DVs. */
interface Candidato {
  digitos: string;
  tipo: 'bancario' | 'convenio';
}

/** A linha digitável bancária: cinco campos com a forma `NNNNN.NNNNN NNNNN.NNNNNN NNNNN.NNNNNN D FFFFVVVVVVVVVV`. */
const PADRAO_BANCARIO =
  /(?<!\d)\d{5}\.?\d{5}\s*\d{5}\.?\d{6}\s*\d{5}\.?\d{6}\s*\d\s*\d{14}(?!\d)/;

/** A linha digitável de convênio: quatro blocos de 11 dígitos, cada um com DV. */
const PADRAO_CONVENIO = /(?<!\d)\d{11}-?\d\s*\d{11}-?\d\s*\d{11}-?\d\s*\d{11}-?\d(?!\d)/;

/** Data `DD/MM/AAAA`. */
const PADRAO_DATA = /(\d{2})\/(\d{2})\/(\d{4})/;

/** Nome dos bancos mais comuns no escritório; os demais saem como `Banco NNN`. */
const BANCOS: Readonly<Record<string, string>> = {
  '001': 'Banco do Brasil',
  '033': 'Santander',
  '077': 'Inter',
  '104': 'Caixa',
  '237': 'Bradesco',
  '260': 'Nubank',
  '336': 'C6 Bank',
  '341': 'Itaú',
  '748': 'Sicredi',
  '756': 'Sicoob',
};

/** Dia zero do fator de vencimento: 07/10/1997, em UTC. */
const BASE_DO_FATOR = Date.UTC(1997, 9, 7);

const MILISSEGUNDOS_POR_DIA = 86_400_000;

/** Dias a somar ao fator de 1000 a 9999 para chegar à data do ciclo atual: fator 1000 vira 22/02/2025. */
const DIAS_DO_REINICIO = 9000;

/**
 * Confere os dígitos verificadores de uma linha digitável.
 *
 * Quem chama é `lerBoleto`, antes de aceitar qualquer código. A linha de 47
 * dígitos confere os três campos com DV módulo 10 e o DV geral módulo 11 sobre
 * os 43 dígitos na ordem do código de barras. A de 48 dígitos precisa começar
 * por `8`, ter identificador de valor 6 a 9 e conferir cada bloco pelo módulo
 * que esse identificador indica.
 *
 * @param digitos - Só os dígitos da linha, sem pontos, espaços nem hífens.
 * @returns `true` quando o comprimento e todos os DVs conferem; `false` em
 *   qualquer outro caso, inclusive texto com caractere que não seja dígito.
 */
export function validarLinhaDigitavel(digitos: string): boolean {
  if (!/^\d+$/.test(digitos)) return false;
  if (digitos.length === 47) return validarBancario(digitos);
  if (digitos.length === 48) return validarConvenio(digitos);
  return false;
}

/**
 * Lê o código, o valor, o vencimento e o banco de um boleto a partir das linhas do PDF.
 *
 * Quem chama é a importação de boleto, depois de `extrairTextoDoPdf` devolver o
 * texto em linhas. A primeira linha digitável com DVs conferidos vence; quando
 * há linhas candidatas e nenhuma confere, o código fica `null` e o aviso diz
 * que os dígitos não conferem. Sem nenhum candidato, o aviso diz que não há
 * linha digitável no texto, e a leitura fica para o provedor.
 *
 * @param linhas - As linhas de texto do PDF, na ordem em que aparecem.
 * @returns O boleto lido, com os avisos de cada campo que ficou sem valor.
 */
export function lerBoleto(linhas: string[]): BoletoLido {
  const candidatos = linhas.flatMap(encontrarCandidatos);
  const escolhido = candidatos.find((candidato) => validarLinhaDigitavel(candidato.digitos));

  if (escolhido === undefined) {
    const aviso =
      candidatos.length === 0
        ? 'Nenhuma linha digitável de boleto foi encontrada no texto.'
        : 'Linha digitável com dígito verificador inválido; código e valor não foram lidos.';
    return {
      codigo: null,
      tipoCodigo: null,
      valor: null,
      vencimento: null,
      banco: null,
      avisos: [aviso],
    };
  }

  return escolhido.tipo === 'bancario'
    ? lerBancario(escolhido.digitos)
    : lerConvenio(escolhido.digitos, linhas);
}

/** Os códigos que casam com a forma da linha digitável, bancária ou de convênio, em cada linha. */
function encontrarCandidatos(linha: string): Candidato[] {
  const bancario = PADRAO_BANCARIO.exec(linha);
  if (bancario !== null) {
    return [{ digitos: bancario[0].replace(/\D/g, ''), tipo: 'bancario' }];
  }
  const convenio = PADRAO_CONVENIO.exec(linha);
  if (convenio !== null) {
    return [{ digitos: convenio[0].replace(/\D/g, ''), tipo: 'convenio' }];
  }
  return [];
}

/** Os três campos com DV módulo 10 e o DV geral módulo 11 do bancário. */
function validarBancario(digitos: string): boolean {
  const camposConferem =
    dvModulo10(digitos.slice(0, 9)) === Number(digitos[9]) &&
    dvModulo10(digitos.slice(10, 20)) === Number(digitos[20]) &&
    dvModulo10(digitos.slice(21, 31)) === Number(digitos[31]);
  return camposConferem && dvGeralBancario(digitos) === Number(digitos[32]);
}

/** Identificador de valor 6 a 9 e cada bloco de 12 dígitos conferindo com o módulo dele. */
function validarConvenio(digitos: string): boolean {
  if (digitos[0] !== '8') return false;
  const modulo = moduloDoConvenio(digitos[2]);
  if (modulo === null) return false;
  return [0, 12, 24, 36].every((inicio) => {
    const base = digitos.slice(inicio, inicio + 11);
    const dv = modulo === 10 ? dvModulo10(base) : dvModulo11Convenio(base);
    return dv === Number(digitos[inicio + 11]);
  });
}

/** O módulo indicado pelo identificador de valor, ou `null` quando ele não é 6 a 9. */
function moduloDoConvenio(identificador: string | undefined): 10 | 11 | null {
  if (identificador === '6' || identificador === '7') return 10;
  if (identificador === '8' || identificador === '9') return 11;
  return null;
}

/** Monta o bancário: código, valor, vencimento pelo fator e banco. */
function lerBancario(digitos: string): BoletoLido {
  const avisos: string[] = [];
  const valorCentavos = Number(digitos.slice(37, 47));
  const valor = valorCentavos === 0 ? null : valorCentavos / 100;
  if (valor === null) {
    avisos.push('O código não traz valor; preencha o valor na conferência.');
  }

  const fator = Number(digitos.slice(33, 37));
  const vencimento = vencimentoDoFator(fator, avisos);

  const codigoBanco = digitos.slice(0, 3);
  return {
    codigo: digitos,
    tipoCodigo: 'bancario',
    valor,
    vencimento,
    banco: BANCOS[codigoBanco] ?? `Banco ${codigoBanco}`,
    avisos,
  };
}

/** Monta o convênio: código e valor pelo identificador, vencimento pelo texto. */
function lerConvenio(digitos: string, linhas: string[]): BoletoLido {
  const avisos: string[] = [];
  const identificador = digitos[2];
  let valor: number | null = null;

  if (identificador === '6' || identificador === '8') {
    const semDvs = [0, 12, 24, 36]
      .map((inicio) => digitos.slice(inicio, inicio + 11))
      .join('');
    const valorCentavos = Number(semDvs.slice(4, 15));
    if (valorCentavos === 0) {
      avisos.push(
        'O código não traz valor (valor ao fornecedor); preencha o valor na conferência.',
      );
    } else {
      valor = valorCentavos / 100;
    }
  } else {
    avisos.push(
      'O código traz valor de referência, não o valor a cobrar; preencha o valor na conferência.',
    );
  }

  const vencimento = vencimentoDoTexto(linhas);
  if (vencimento === null) {
    avisos.push(
      'Vencimento não encontrado perto de "Vencimento" no texto; preencha na conferência.',
    );
  }

  return {
    codigo: digitos,
    tipoCodigo: 'convenio',
    valor,
    vencimento,
    banco: null,
    avisos,
  };
}

/**
 * Converte o fator de vencimento em data `AAAA-MM-DD`.
 *
 * Quem chama é `lerBancario`. O fator 0000 e os fatores de 1 a 999 devolvem
 * `null` com aviso, porque depois do reinício eles não ocorrem e não têm data
 * segura. Os fatores de 1000 a 9999 são somados a 9000 dias da base 07/10/1997.
 *
 * @param fator - Os quatro dígitos do fator, como número.
 * @param avisos - A lista de avisos do boleto, onde o aviso deste campo entra.
 * @returns A data em `AAAA-MM-DD`, ou `null` quando o fator não tem data.
 */
function vencimentoDoFator(fator: number, avisos: string[]): string | null {
  if (fator === 0) {
    avisos.push('O código não traz vencimento (fator 0000).');
    return null;
  }
  if (fator < 1000) {
    avisos.push(
      'Fator de vencimento fora da faixa esperada; preencha o vencimento na conferência.',
    );
    return null;
  }
  const data = new Date(BASE_DO_FATOR + (fator + DIAS_DO_REINICIO) * MILISSEGUNDOS_POR_DIA);
  return data.toISOString().slice(0, 10);
}

/**
 * Acha a primeira data `DD/MM/AAAA` na linha de "Vencimento" ou na seguinte.
 *
 * Quem chama é `lerConvenio`, porque o convênio não traz a data no código. Uma
 * data que não existe, como 31/02, não conta, e a busca segue para a próxima
 * ocorrência de "Vencimento".
 *
 * @param linhas - As linhas do PDF, na ordem em que aparecem.
 * @returns A data em `AAAA-MM-DD`, ou `null` quando nenhuma data válida está perto.
 */
function vencimentoDoTexto(linhas: string[]): string | null {
  for (const [indice, linha] of linhas.entries()) {
    if (!/vencimento/i.test(linha)) continue;
    const trecho = `${linha} ${linhas[indice + 1] ?? ''}`;
    const data = PADRAO_DATA.exec(trecho);
    if (data === null) continue;
    const dia = Number(data[1]);
    const mes = Number(data[2]);
    const ano = Number(data[3]);
    const existe = new Date(Date.UTC(ano, mes - 1, dia));
    if (existe.getUTCDate() === dia && existe.getUTCMonth() === mes - 1) {
      return `${data[3] ?? ''}-${data[2] ?? ''}-${data[1] ?? ''}`;
    }
  }
  return null;
}

/** DV módulo 10 de um campo: pesos 2 e 1 a partir da direita, soma dos algarismos dos produtos. */
function dvModulo10(base: string): number {
  let soma = 0;
  let peso = 2;
  for (let indice = base.length - 1; indice >= 0; indice--) {
    const produto = Number(base[indice]) * peso;
    soma += produto > 9 ? Math.floor(produto / 10) + (produto % 10) : produto;
    peso = peso === 2 ? 1 : 2;
  }
  return (10 - (soma % 10)) % 10;
}

/**
 * DV geral do bancário: módulo 11 sobre os 43 dígitos do código de barras.
 *
 * O resto 0, 1 ou 10 vira 1, porque o DV é `11 - resto` e só o intervalo de 2 a
 * 9 é usado tal como sai da conta.
 */
function dvGeralBancario(digitos: string): number {
  const barra =
    digitos.slice(0, 4) +
    digitos.slice(33, 47) +
    digitos.slice(4, 9) +
    digitos.slice(10, 20) +
    digitos.slice(21, 31);
  const dv = dvModulo11(barra);
  return dv > 9 ? 1 : dv;
}

/** DV módulo 11 do bloco de convênio: `11 - resto`, com 10 e 11 virando 0. */
function dvModulo11Convenio(base: string): number {
  const dv = dvModulo11(base);
  return dv > 9 ? 0 : dv;
}

/** Soma dos dígitos com pesos 2 a 9 a partir da direita, e `11 - resto`, sem tratamento dos casos 10 e 11. */
function dvModulo11(base: string): number {
  let soma = 0;
  let peso = 2;
  for (let indice = base.length - 1; indice >= 0; indice--) {
    soma += Number(base[indice]) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  return 11 - (soma % 11);
}
